import { useRef, useState } from "react";
import type { AdminProject } from "../../api/admin";
import { ProjectGlyphTile } from "./MemberParts";
import { IconSend, IconShield, IconUser, IconX } from "./icons";
import { useDialogFocus } from "./useDialogFocus";

export type InvitePayload = {
  emails: string[];
  role: "admin" | "member";
  projectIds: string[];
  monthlyDollars: number | null;
};

type Props = {
  projects: AdminProject[];
  onClose: () => void;
  onInvite: (payload: InvitePayload) => Promise<void>;
};

const EMAIL_RE = /^\S+@\S+\.\S+$/;

/**
 * Invite modal. Maps onto the daemon's `createUser` (one call per email) plus
 * optional `setUserLimits` / `setUserProjects` follow-ups — handled by the
 * orchestrator, which surfaces the one-time temp passwords on success.
 */
export function InviteModal({ projects, onClose, onInvite }: Props) {
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus the email field, trap Tab, close on Escape, restore focus on close.
  useDialogFocus(modalRef, onClose, inputRef);

  const addEmail = (raw: string) => {
    const e = raw.trim().replace(/,$/, "").toLowerCase();
    if (e && EMAIL_RE.test(e) && !emails.includes(e)) setEmails((x) => [...x, e]);
  };

  const onDraftKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === "," || e.key === " ") && draft.trim()) {
      e.preventDefault();
      addEmail(draft);
      setDraft("");
    } else if (e.key === "Backspace" && !draft && emails.length) {
      setEmails((x) => x.slice(0, -1));
    }
  };

  const togglePick = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const pendingDraft = draft.trim() && EMAIL_RE.test(draft.trim()) ? 1 : 0;
  const count = emails.length + pendingDraft;

  const send = async () => {
    const list = [...emails];
    const d = draft.trim().toLowerCase();
    if (d && EMAIL_RE.test(d) && !list.includes(d)) list.push(d);
    const unique = [...new Set(list)];
    if (!unique.length) {
      setError("Add at least one valid email address.");
      return;
    }
    const t = budget.trim();
    let monthlyDollars: number | null = null;
    if (t !== "") {
      const n = Number(t);
      if (!Number.isFinite(n) || n < 0) {
        setError("Budget must be a positive number, or blank for unlimited.");
        return;
      }
      monthlyDollars = n;
    }

    setBusy(true);
    setError(null);
    try {
      await onInvite({ emails: unique, role, projectIds: [...picked], monthlyDollars });
      // On success the orchestrator unmounts this modal and shows credentials.
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div ref={modalRef} className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Invite to workspace">
        <div className="modal-head">
          <span className="bolt" aria-hidden="true" />
          <h3>Invite to workspace</h3>
          <button className="sheet-close" onClick={onClose} aria-label="Close">
            <IconX />
          </button>
        </div>

        <div className="modal-body">
          <div className="mfield">
            <label htmlFor="invite-emails">Email addresses</label>
            <div className="email-chips" onClick={() => inputRef.current?.focus()}>
              {emails.map((e) => (
                <span key={e} className="email-chip">
                  {e}
                  <button aria-label={`Remove ${e}`} onClick={() => setEmails((x) => x.filter((y) => y !== e))}>
                    <IconX style={{ width: 11, height: 11 }} />
                  </button>
                </span>
              ))}
              <input
                id="invite-emails"
                ref={inputRef}
                value={draft}
                placeholder={emails.length ? "" : "name@thunderay.com, …"}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onDraftKey}
                onBlur={() => {
                  if (draft.trim()) {
                    addEmail(draft);
                    setDraft("");
                  }
                }}
              />
            </div>
          </div>

          <div className="mfield">
            <label>Role</label>
            <div className="role-seg">
              <button className={`admin${role === "admin" ? " on" : ""}`} onClick={() => setRole("admin")}>
                <IconShield /> Admin
              </button>
              <button className={`member${role === "member" ? " on" : ""}`} onClick={() => setRole("member")}>
                <IconUser /> Member
              </button>
            </div>
          </div>

          {projects.length > 0 && (
            <div className="mfield">
              <label>Project access</label>
              <div className="access-grid">
                {projects.map((p) => {
                  const on = picked.has(p.id);
                  return (
                    <div
                      key={p.id}
                      className={`access-pick${on ? " on" : ""}`}
                      role="checkbox"
                      aria-checked={on}
                      aria-label={`Toggle access to ${p.name}`}
                      tabIndex={0}
                      onClick={() => togglePick(p.id)}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          togglePick(p.id);
                        }
                      }}
                    >
                      <ProjectGlyphTile name={p.name} kind={p.kind} />
                      <span className="ap-name">{p.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mfield">
            <label>Monthly token budget</label>
            <div className="budget-input" style={{ alignSelf: "flex-start" }}>
              <span className="cur">$</span>
              <input
                value={budget}
                placeholder="∞"
                inputMode="decimal"
                aria-label="Monthly budget (blank = unlimited)"
                onChange={(e) => setBudget(e.target.value.replace(/[^0-9.]/g, ""))}
              />
              <span className="per">/ mo</span>
            </div>
          </div>

          {error && <p className="modal-error">{error}</p>}
        </div>

        <div className="modal-foot">
          <span className="seat-note">
            {count || "no"} invite{count === 1 ? "" : "s"} · seats unlimited
          </span>
          <span className="spacer" />
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-accent" onClick={send} disabled={busy || count === 0}>
            <IconSend style={{ width: 13, height: 13 }} />
            {busy ? "Creating…" : `Send invite${count > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
